/**
 * Nombre de la compañía en el cliente.
 *
 * Se usa allí donde la interfaz tiene que hablar de "la empresa" como una parte
 * más frente a las personas: en Gastos, el selector de quién puso el dinero dice
 * "JR2" en vez de "La empresa (banco)", que es como lo llama Jesús al hablar.
 *
 * POR QUÉ NO ES UN LITERAL. Escribir "JR2" en el fichero de traducciones lo
 * dejaría también en la demo y en cualquier otra instalación de esta app, que no
 * se llaman así. El nombre sale de Ajustes → Compañía, que es su único dueño.
 *
 * Mismo patrón de caché de módulo que `service-lines.ts`: una llamada por sesión
 * compartida entre componentes. Si falla, quien lo use cae a su literal genérico
 * — un nombre que no carga no debe dejar un hueco en la pantalla.
 */

import { useEffect, useState } from 'react'
import { api } from './api'

let cache: string | null = null
let inflight: Promise<string | null> | null = null

async function fetchFirmName(): Promise<string | null> {
  if (cache) return cache
  if (!inflight) {
    inflight = api
      .get<{ firm: { name?: string | null } | null }>('/firm')
      .then((r) => {
        cache = r.firm?.name?.trim() || null
        return cache
      })
      .catch(() => null)
      .finally(() => {
        inflight = null
      })
  }
  return inflight
}

export function invalidateFirmNameCache() {
  cache = null
}

/** El nombre de la compañía, o `null` mientras carga o si no se pudo leer. */
export function useFirmName(): string | null {
  const [name, setName] = useState<string | null>(cache)
  useEffect(() => {
    if (cache) return
    let cancelled = false
    void fetchFirmName().then((n) => {
      if (!cancelled) setName(n)
    })
    return () => {
      cancelled = true
    }
  }, [])
  return name
}
